import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { isNonEmptyString } from 'ts-utils';
import { Color } from '../../config/Color';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { ScreenWrapper } from '../styles/Containers';
import { PaddedAccentContainer } from '../styles/ColoredContainers';
import { BodySM, Heading3 } from '../styles/Typography';
import { NavigationProps } from './FlowNavigation';
import {
  ButtonContainer,
  PresentationContent,
  PresentationLayout,
} from './layouts/PresentationLayout';

type ProgramOverviewLayoutConfig = {
  title: string;
  /** Optional intro line rendered above the stat card (question-style). */
  heading?: string;
  /** Optional paragraph(s) rendered between the stat card and the program items. */
  body?: string[];
  statisticHighlight: string;
  statistic: string;
  programItems: Array<{ icon: string; label: string }>;
  /** Small print rendered inline, with any marker (e.g. `*`) written into the string itself. */
  footnote?: string;
  /** Citations rendered below a divider and auto-numbered, matching `educationCotreatments`. */
  footnotes?: string[];
  primaryButtonTitle: string;
};

export type ProgramOverviewScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.ProgramOverview,
  ProgramOverviewLayoutConfig,
  ScreenNames
>;

type ProgramOverviewProps<ScreenNames extends string> = NavigationProps<
  ProgramOverviewScreenConfig<ScreenNames>,
  ScreenNames
>;

// --- Styled components ---

const StatCard = styled(PaddedAccentContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
`;

const StatHighlight = styled.span`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${Color.content.default};
  line-height: 1;
`;

const StatText = styled(BodySM)`
  margin: 0;
`;

const SectionHeading = styled(Heading3)`
  margin: ${({ theme }) => `${theme.spacing._32px} 0 ${theme.spacing._8px}`};
`;

// The shared ButtonContainer's 40px top padding is right for the presentation screens, but leaves
// the CTA floating away from the program-items card here, so it's tightened for this screen only.
const ProgramOverviewWrapper = styled(ScreenWrapper)`
  ${ButtonContainer} {
    padding-top: ${({ theme }) => theme.spacing._16px};
  }
`;

const ProgramItemsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProgramItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IconImg = styled.img`
  width: 20px;
  height: 20px;
`;

const ItemLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.4;
  color: ${Color.content.default};
`;

const TopLine = styled(BodySM)`
  text-align: center;
`;

const BodyParagraph = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.4;
  color: ${Color.content.default};
  text-align: center;
`;

// --- Screen component ---

export const ProgramOverviewScreen = <ScreenNames extends string>(
  props: ProgramOverviewProps<ScreenNames>
): ReactElement => {
  const { navigation, layout } = props;

  function handlePrimaryButtonPress() {
    navigation.next(props);
  }

  const statCard = (
    <StatCard borderRadius>
      <StatHighlight>{layout.statisticHighlight}</StatHighlight>
      <StatText>{layout.statistic}</StatText>
    </StatCard>
  );

  const programItemsCard = (
    <PaddedAccentContainer borderRadius>
      <ProgramItemsList>
        {layout.programItems.map((item, index) => (
          <ProgramItem key={index}>
            <IconCircle>
              <IconImg src={item.icon} alt="" />
            </IconCircle>
            <ItemLabel>{item.label}</ItemLabel>
          </ProgramItem>
        ))}
      </ProgramItemsList>
    </PaddedAccentContainer>
  );

  const presentationContent: PresentationContent[] = [
    ...(isNonEmptyString(layout.heading)
      ? [{ type: 'custom' as const, content: <TopLine>{layout.heading}</TopLine> }]
      : []),
    { type: 'custom', content: statCard },
    ...(layout.body?.map((text, index) => ({
      type: 'custom' as const,
      content: <BodyParagraph key={index}>{text}</BodyParagraph>,
    })) ?? []),
    ...(layout.title !== ''
      ? [{ type: 'custom' as const, content: <SectionHeading>{layout.title}</SectionHeading> }]
      : []),
    { type: 'custom', content: programItemsCard },
    ...(isNonEmptyString(layout.footnote)
      ? [{ type: 'smalltext' as const, content: layout.footnote }]
      : []),
  ];

  return (
    <ProgramOverviewWrapper>
      <PresentationLayout
        content={presentationContent}
        footnotes={layout.footnotes}
        primaryButtonTitle={layout.primaryButtonTitle}
        onPressPrimaryButton={handlePrimaryButtonPress}
      />
    </ProgramOverviewWrapper>
  );
};
